import { BasicBusinessHoursFragment, StoreHoliday } from 'drycut-graphql';
import { DateTime } from 'luxon';

type DaySchedule = {
  start: DateTime;
  end: DateTime;
  closed: boolean;
  holiday: boolean;
};

type ValidWeekDay =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';

type ValidWeekDayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export function getDaySchedule(
  date: DateTime,
  businessHours: BasicBusinessHoursFragment,
): DaySchedule {
  // Check if the date is a holiday
  const holiday = getHoliday(date, businessHours);

  // If the date is an holiday we need to override any schedule to use the holiday schedule
  if (holiday) {
    const startTime = getHourMinuteFromString(holiday.startTime);
    const endTime = getHourMinuteFromString(holiday.endTime);

    return {
      start: date.set({
        hour: startTime.hour,
        minute: startTime.minute,
      }),
      end: date.set({
        hour: endTime.hour,
        minute: endTime.minute,
      }),
      closed: holiday.closed,
      holiday: true,
    };
  }

  // We need to convert the date as it can be in the client's locale and weekdays have different
  // names. In the DB we store the weekdays in English
  const weekDay: ValidWeekDay = date.setLocale('en-US').weekdayLong as ValidWeekDay;
  const weekDayKey: ValidWeekDayKey = weekDay.toLowerCase() as ValidWeekDayKey;

  // Get the schedule for provided day, read from the businessHours object
  const dateSchedule = businessHours[weekDayKey];
  const closed = dateSchedule.closed;
  const startTime = getHourMinuteFromString(dateSchedule.startTime);
  const endTime = getHourMinuteFromString(dateSchedule.endTime);

  return {
    start: date.set({
      hour: startTime.hour,
      minute: startTime.minute,
    }),
    end: date.set({
      hour: endTime.hour,
      minute: endTime.minute,
    }),
    closed,
    holiday: false,
  };
}

// Returns an object with the hour and minute from a string in the format "HH:MM"
function getHourMinuteFromString(time: string): { hour: number; minute: number } {
  const [hour, minute] = time.split(':').map((part) => parseInt(part, 10));

  return { hour, minute };
}

// Returns the holiday information (if any) for the provided date
export function getHoliday(
  date: DateTime,
  businessHours: BasicBusinessHoursFragment,
): StoreHoliday | undefined {
  const holiday = businessHours.holidays.find((holiday) =>
    DateTime.fromISO(holiday.date).hasSame(date, 'day'),
  );

  return holiday;
}
